/**
 * FLUXXYAERO™
 * Global Theme JavaScript
 * Dawn-compatible
 *
 * Handles:
 * - Scroll reveal
 * - Announcement transitions
 * - Keyboard accessibility
 * - Bundle quantity selection
 * - Safe Shopify section reinitialization
 * - Reduced-motion support
 */

(() => {
  'use strict';

  const FX = {
    initialized: false,

    prefersReducedMotion() {
      return window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    init() {
      this.initReveal();
      this.initAnnouncement();
      this.initKeyboardFocus();
      this.initBundles();
      this.initShopifyEvents();

      this.initialized = true;
    },

    /* ----------------------------------------
       SCROLL REVEAL
    ---------------------------------------- */

    initReveal() {
      const elements = document.querySelectorAll(
        '[data-fx-reveal]:not([data-fx-reveal-ready])'
      );

      if (!elements.length) return;

      elements.forEach((element) => {
        element.setAttribute('data-fx-reveal-ready', 'true');

        if (this.prefersReducedMotion()) {
          element.classList.add('fx-is-visible');
        } else {
          element.classList.add('fx-reveal-hidden');
        }
      });

      if (
        this.prefersReducedMotion() ||
        !('IntersectionObserver' in window)
      ) {
        elements.forEach((element) => {
          element.classList.add('fx-is-visible');
        });

        return;
      }

      const observer = new IntersectionObserver(
        (entries, currentObserver) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add('fx-is-visible');
            currentObserver.unobserve(entry.target);
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -8% 0px',
          threshold: 0.08
        }
      );

      elements.forEach((element) => {
        observer.observe(element);
      });
    },

    /* ----------------------------------------
       ANNOUNCEMENT BAR
    ---------------------------------------- */

    initAnnouncement() {
      const bars = document.querySelectorAll(
        '[data-fx-announcement]:not([data-fx-announcement-ready])'
      );

      if (!bars.length) return;

      bars.forEach((bar) => {
        bar.setAttribute(
          'data-fx-announcement-ready',
          'true'
        );

        const items = Array.from(
          bar.querySelectorAll(
            '[data-fx-announcement-item]'
          )
        );

        if (items.length <= 1) return;

        let currentIndex = 0;

        items.forEach((item, index) => {
          item.classList.toggle(
            'fx-is-active',
            index === currentIndex
          );
        });

        if (this.prefersReducedMotion()) return;

        const speed =
          Number(
            bar.dataset.fxAnnouncementSpeed
          ) || 4000;

        window.setInterval(() => {
          if (!items[currentIndex]) return;

          items[currentIndex].classList.remove(
            'fx-is-active'
          );

          currentIndex =
            (currentIndex + 1) % items.length;

          items[currentIndex].classList.add(
            'fx-is-active'
          );
        }, Math.max(speed, 2000));
      });
    },

    /* ----------------------------------------
       KEYBOARD ACCESSIBILITY
    ---------------------------------------- */

    initKeyboardFocus() {
      const body = document.body;

      if (
        !body ||
        body.dataset.fxFocusReady === 'true'
      ) {
        return;
      }

      body.dataset.fxFocusReady = 'true';

      let usingKeyboard = false;

      document.addEventListener('keydown', (event) => {
        if (
          event.key === 'Tab' ||
          event.key === 'ArrowUp' ||
          event.key === 'ArrowDown' ||
          event.key === 'ArrowLeft' ||
          event.key === 'ArrowRight'
        ) {
          usingKeyboard = true;

          document.documentElement.classList.add(
            'fx-keyboard-user'
          );
        }
      });

      document.addEventListener('mousedown', () => {
        usingKeyboard = false;

        document.documentElement.classList.remove(
          'fx-keyboard-user'
        );
      });

      document.addEventListener('focusin', (event) => {
        if (!usingKeyboard) return;

        const target = event.target;

        if (!target || !target.matches) return;

        if (
          target.matches(
            'a, button, input, select, textarea, [tabindex]'
          )
        ) {
          target.classList.add(
            'fx-keyboard-focus'
          );
        }
      });

      document.addEventListener('focusout', (event) => {
        if (
          !event.target ||
          !event.target.classList
        ) {
          return;
        }

        event.target.classList.remove(
          'fx-keyboard-focus'
        );
      });
    },

    /* ----------------------------------------
       BUNDLE SELECTOR
    ---------------------------------------- */

    initBundles() {
      const bundles = document.querySelectorAll(
        '[data-fx-bundle]:not([data-fx-bundle-ready])'
      );

      if (!bundles.length) return;

      bundles.forEach((bundle) => {
        bundle.setAttribute(
          'data-fx-bundle-ready',
          'true'
        );

        const options = bundle.querySelectorAll(
          '[data-fx-bundle-quantity]'
        );

        const quantityInput =
          bundle.querySelector(
            '[data-fx-bundle-quantity-input]'
          );

        if (!options.length || !quantityInput) {
          return;
        }

        options.forEach((option) => {
          option.addEventListener(
            'change',
            () => {
              if (!option.checked) return;

              const quantity =
                Number(option.value) || 1;

              quantityInput.value = quantity;

              const labels =
                bundle.querySelectorAll(
                  '.fx-bundle__option'
                );

              labels.forEach((label) => {
                const input =
                  label.querySelector(
                    '[data-fx-bundle-quantity]'
                  );

                label.classList.toggle(
                  'fx-bundle__option--active',
                  input && input.checked
                );
              });

              bundle.dispatchEvent(
                new CustomEvent(
                  'fluxxy:bundle-change',
                  {
                    bubbles: true,
                    detail: {
                      quantity: quantity
                    }
                  }
                )
              );
            }
          );
        });
      });
    },

    /* ----------------------------------------
       SHOPIFY SECTION EVENTS
    ---------------------------------------- */

    initShopifyEvents() {
      document.addEventListener(
        'shopify:section:load',
        (event) => {
          if (!event.target) return;

          this.initReveal();
          this.initAnnouncement();
          this.initBundles();
        }
      );

      document.addEventListener(
        'shopify:section:reorder',
        () => {
          this.initReveal();
        }
      );

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!event.target) return;

          event.target.classList.add(
            'fx-editor-selected'
          );
        }
      );

      document.addEventListener(
        'shopify:block:deselect',
        (event) => {
          if (!event.target) return;

          event.target.classList.remove(
            'fx-editor-selected'
          );
        }
      );
    }
  };

  /* ----------------------------------------
     INITIAL LOAD
  ---------------------------------------- */

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => FX.init(),
      { once: true }
    );
  } else {
    FX.init();
  }

  /* ----------------------------------------
     PUBLIC ACCESS
  ---------------------------------------- */

  window.FluxxyAero = FX;

})();